// services/user.js
import api from './api';

// Normalize axios errors to Error(message) while keeping structured payload for UI hints.
const asError = (err, fallback = 'Request failed') => {
  const responseData = err?.response?.data;
  const msg =
    responseData?.error ||
    responseData?.message ||
    err?.message ||
    fallback;
  const error = new Error(msg);
  if (responseData && typeof responseData === 'object') {
    error.details = responseData;
  }
  if (err?.response?.status) {
    error.status = err.response.status;
  }
  return error;
};

// Strip tutor/subject assignment fields unless role is 'student'.
// Also normalizes arrays for students and removes falsy/duplicates.
const sanitizeAssignmentPayload = (data) => {
  if (!data || typeof data !== 'object') return data;

  const role = data.role;
  const out = { ...data };

  if (role !== 'student') {
    delete out.tutorIds;
    delete out.subjectIds;
  } else {
    if (!Array.isArray(out.tutorIds)) out.tutorIds = [];
    if (!Array.isArray(out.subjectIds)) out.subjectIds = [];
    out.tutorIds = [...new Set(out.tutorIds.filter(Boolean))];
    out.subjectIds = [...new Set(out.subjectIds.filter(Boolean))];
  }

  return out;
};

// Optional helper
export const getUser = async (userId) => {
  try {
    const res = await api.get(`/users/${userId}`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to fetch user');
  }
};

// Fetch users (optionally filter by role: 'student' | 'tutor' | 'admin')
export const fetchUsers = async (options) => {
  try {
    let params;
    if (typeof options === 'string') {
      params = { role: options };
    } else if (options && typeof options === 'object') {
      const tmp = {};
      if (options.role) tmp.role = options.role;
      if (options.program) tmp.program = options.program;
      if (options.portal) tmp.portal = options.portal;
      if (options.subjectId) tmp.subjectId = options.subjectId;
      [
        'curriculum',
        'curricula',
        'grade',
        'grades',
        'subject',
        'subjects',
        'province',
        'suburb',
        'town',
        'area',
        'areaCode',
        'postalCode',
        'postalCodes',
        'country',
        'deliveryMethod',
        'deliveryMethods',
        'status',
      ].forEach((key) => {
        const value = options[key];
        if (Array.isArray(value) && value.length) tmp[key] = value.join(',');
        else if (typeof value === 'string' && value.trim()) tmp[key] = value.trim();
        else if (value !== undefined && value !== null && typeof value !== 'string' && !Array.isArray(value)) tmp[key] = value;
      });
      if (Object.keys(tmp).length > 0) params = tmp;
    }

    const res = await api.get('/users', { params });
    return res.data;
  } catch (err) {
    console.error('Failed to fetch users:', err);
    throw asError(err, 'Failed to fetch users');
  }
};

export const fetchTutorApplicationDocuments = async (userId) => {
  try {
    const res = await api.get(`/users/${userId}/tutor-application-documents`);
    return res.data?.item || null;
  } catch (err) {
    throw asError(err, 'Failed to fetch tutor application documents');
  }
};

export const downloadTutorProfileDocument = async (userId, documentType, options = {}) => {
  try {
    const res = await api.get(`/users/${userId}/tutor-profile-documents/${encodeURIComponent(documentType)}`, {
      params: options,
      responseType: 'blob',
    });
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to download tutor profile document');
  }
};

export const fetchPortalRequirements = async () => {
  try {
    const res = await api.get('/users/portal-requirements');
    return res.data || {};
  } catch (err) {
    throw asError(err, 'Failed to fetch portal requirements');
  }
};

//  Register a new user (admin creates student/tutor)
// Important: use /users (our custom controller), not /auth/register
export const registerUser = async (userData) => {
  if (!userData || typeof userData !== 'object') {
    throw new Error('Invalid user data provided');
  }
  try {
    const payload = sanitizeAssignmentPayload(userData);
    const res = await api.post('/users', payload); // <-- changed
    return res.data; // backend returns full user (without password)
  } catch (err) {
    console.error('User registration failed:', err);
    throw asError(err, 'User registration failed');
  }
};

// Update existing user by ID (returns fresh user)
export const updateUser = async (userId, updatedData) => {
  if (!userId) throw new Error('User ID is required');
  if (!updatedData || typeof updatedData !== 'object') {
    throw new Error('Invalid update data provided');
  }

  try {
    const payload = sanitizeAssignmentPayload(updatedData);
    await api.put(`/users/${userId}`, payload);
    const res = await api.get(`/users/${userId}`);
    return res.data;
  } catch (err) {
    console.error(`Failed to update user ${userId}:`, err);
    throw asError(err, 'Failed to update user');
  }
};

// Delete user by ID
export const deleteUser = async (userId) => {
  if (!userId) throw new Error('User ID is required');

  try {
    const res = await api.delete(`/users/${userId}`);
    return res.data;
  } catch (err) {
    console.error(`Failed to delete user ${userId}:`, err);
    throw asError(err, 'Failed to delete user');
  }
};

export const createAdminStudent = async (studentData, idempotencyKey) => {
  if (!studentData || typeof studentData !== 'object') throw new Error('Invalid student data provided');
  if (!idempotencyKey) throw new Error('Student creation request key is required');
  try {
    const res = await api.post('/admin/students', studentData, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return res.data;
  } catch (err) {
    throw asError(err, 'Student creation failed');
  }
};

export const fetchAdminStudentTutorEligibility = async (studentData) => {
  try {
    const res = await api.post('/admin/students/tutor-eligibility', studentData);
    return Array.isArray(res.data?.items) ? res.data.items : [];
  } catch (err) {
    throw asError(err, 'Tutor eligibility check failed');
  }
};

export const resendAdminStudentActivation = async (studentId) => {
  if (!studentId) throw new Error('Student ID is required');
  try {
    const res = await api.post(`/admin/students/${studentId}/resend-activation`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Unable to resend student activation');
  }
};

export const createTutorProfileShareLink = async (userId) => {
  if (!userId) throw new Error('User ID is required');
  try {
    const res = await api.post(`/users/${userId}/tutor-profile-share`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to create tutor profile share link');
  }
};

export const disableTutorProfileShareLink = async (userId) => {
  if (!userId) throw new Error('User ID is required');
  try {
    const res = await api.post(`/users/${userId}/tutor-profile-share/disable`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to disable tutor profile share link');
  }
};

// Set subjects for a student
export const setUserSubjects = async (userId, subjectIds) => {
  try {
    const res = await api.post(`/users/${userId}/subjects`, { subjectIds });
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to assign subjects');
  }
};

export const resendWelcomeEmail = async (userId, password) => {
  try {
    const res = await api.post(`/users/${userId}/resend-welcome`, password ? { password } : {});
    return res.data;
  } catch (err) {
    console.error('Failed to resend welcome email:', err);
    throw err.response?.data || err;
  }
};

// Admin or self: change password
// Admin can omit currentPassword; students/tutors must provide it when changing their own
export const changeUserPassword = async ({ userId, newPassword, currentPassword }) => {
  if (!userId) throw new Error('User ID is required');
  if (!newPassword || newPassword.length < 8) throw new Error('New password must be at least 8 characters');
  try {
    const payload = currentPassword ? { currentPassword, newPassword } : { newPassword };
    const res = await api.post(`/users/${userId}/change-password`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to change password');
  }
};

// Export users as Blob (csv or json); optional role filter
export const exportUsersFile = async ({ format = 'csv', role } = {}) => {
  const res = await api.get('/users/export', {
    params: { format, role },
    responseType: 'blob',
  });
  return res.data; // Blob
};

// Import users via file (CSV or JSON)
export const importUsersFile = async (file) => {
  if (!file) throw new Error('No file provided');
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post('/users/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data; // { created, updated, skipped, errors[] }
};

// Lock or unlock user accounts
export const lockUserAccount = async (userId, reason) => {
  if (!userId) throw new Error('User ID is required');
  try {
    const payload = reason ? { reason } : {};
    const res = await api.patch(`/users/${userId}/lock`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to lock user');
  }
};

export const unlockUserAccount = async (userId) => {
  if (!userId) throw new Error('User ID is required');
  try {
    const res = await api.patch(`/users/${userId}/unlock`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to unlock user');
  }
};


